'use client';

import moment from 'moment-timezone';

import { RegistrationButton } from '@/modules/league/details/components/RegistrationButton';
import RegistrationInfoBox from '@/modules/league/details/components/RegistrationInfoBox';
import SidebarBoxWithButtons from '@/modules/league/details/components/SidebarBoxWithButtons';
import { useClubMemberStatus } from '@/modules/league/hooks/useClubMemberStatus';
import { useRegistrationStatus } from '@/modules/league/hooks/useRegistrationStatus';
import { LeagueDetails, SessionLeagueDetails } from '@/modules/league/new-components/types';
import { ClubMemberData } from '@/types/leagues.types';
import { Session } from '@/types/types';

export interface DetailsSidebarProps {
  details: LeagueDetails;
  session: SessionLeagueDetails;
  clubMember: ClubMemberData[];
  isRegistered: boolean;
  params: { slug: string; sessionId: string };
  showSponsors: boolean;
  isAuthorized: boolean;
  isSuperAdmin: boolean;
  user?: Session;
}

export function DetailsSidebar({
  details,
  session,
  clubMember,
  isRegistered,
  params,
  showSponsors,
  isAuthorized,
  isSuperAdmin,
  user,
}: DetailsSidebarProps) {
  const isUserLoggedIn = user?.token && user?.pbUuid ? true : false;

  const userTimeInSpecifiedZone = moment().tz(session.Timezone_IanaID).utc();
  const registrationCloseDate = moment.parseZone(session.Registration_DateClosed_UTC);
  const isRegistrationDateAfterNow = userTimeInSpecifiedZone.isAfter(registrationCloseDate);
  const isRegistrationLocked = details.IsLocked;
  const isRegistrationActive = !isRegistrationLocked && !isRegistrationDateAfterNow;
  const isRegistrationOnlyForMembers = !session.AllowNonMemberSignUp;
  const firstPlayerMembershipStatus = session.ClubMemberStatus;

  const registrationButtonStatus = useRegistrationStatus(
    isRegistrationLocked,
    isRegistrationDateAfterNow,
    isRegistrationOnlyForMembers,
    session,
    isRegistered,
    isUserLoggedIn
  );

  const playerMembershipStatus = useClubMemberStatus(
    false,
    isRegistrationOnlyForMembers,
    firstPlayerMembershipStatus
  );

  return (
    <>
      <div className='sm:flex w-full hidden flex-col justify-between rounded-md pb-4 text-sm text-gray-600 md:border md:px-8 md:pt-4 md:text-sm md:shadow-lg h-auto'>
        <RegistrationButton
          isUserLoggedIn={isUserLoggedIn}
          status={registrationButtonStatus}
          session={session}
          playerMembershipStatus={playerMembershipStatus}
        />

        <RegistrationInfoBox
          details={details}
          session={session}
          isRegistered={isRegistered}
          params={params}
          user={user}
        />
      </div>
      <SidebarBoxWithButtons
        details={details}
        session={session}
        clubMember={clubMember}
        isRegistrationActive={isRegistrationActive}
        params={params}
        showSponsors={showSponsors}
        isAuthorized={isAuthorized}
        isSuperAdmin={isSuperAdmin}
        user={user}
      />
    </>
  );
}
